import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getStateForCity } from '../lib/cityStateMap';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import { PAYMENT_MODE_OPTIONS, formatPaymentMode } from '../lib/utils';
import api from '../lib/api';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';
import SecurityWatermark from './SecurityWatermark';
import { isRestricted, maskPhone, maskEmail, maskGST, maskAddress, applyRestrictedSearch, MIN_SEARCH_CHARS } from '../lib/restrictions';
import SearchableSelect from './SearchableSelect';
import { INDIAN_STATES, COUNTRIES } from '../lib/geoData';

const CustomerManagement = ({ user }) => {
  const location = useLocation();
  const navigateTo = useNavigate();
  const returnToSO = useRef(false);
  const [customers, setCustomers] = useState([]);
  const bulk = useBulkSelect();
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCust, setEditingCust] = useState(null);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', phone: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterPaymentModes, setFilterPaymentModes] = useState([]);
  const [filterCreditDays, setFilterCreditDays] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_person: '', phone: '', accountant_phone: '', email: '',
    address: '', area: '', city: '', state: '', country: 'India', pincode: '', gst_number: '',
    transport_preference: '', payment_mode: '', credit_days: '', default_channel: '', wsp_type: 'all', discount_percent: '', agent_id: '', distributor_id: '',
  });

  useEffect(() => { loadData(); }, []);

  // Auto-open create dialog when coming from SO creation
  useEffect(() => {
    if (location.state?.returnToSO) {
      returnToSO.current = true;
      setIsDialogOpen(true);
      toast.info('Create a new customer — you\'ll be taken back to the Sales Order after saving');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadData = async () => {
    try {
      const [custRes, agentRes, distRes] = await Promise.allSettled([
        api.get('/customers'), api.get('/agents'), api.get('/distributors'),
      ]);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (agentRes.status === 'fulfilled') setAgents(agentRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const viewHistory = async (custId) => {
    setHistoryLoading(true);
    setHistoryOpen(true);
    try {
      const res = await api.get(`/customers/${custId}/history`);
      setHistoryData(res.data);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'pincode' && value.length === 6) fetchPincodeDetails(value);
  };

  // Direct field setter (used by SearchableSelect which passes a value, not an event)
  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  // When State changes, clear City if it no longer belongs (keep free-typed values)
  const handleStateChange = (value) => setForm(prev => ({ ...prev, state: value }));

  // Agent / Distributor selection auto-sets the default channel.
  // Both selected => Via Distributor.
  const handleAgentChange = (e) => {
    const agentId = e.target.value;
    setForm(prev => {
      let ch = prev.default_channel;
      if (prev.distributor_id) ch = 'via_distributor';
      else if (agentId) ch = 'via_agent';
      else if (ch === 'via_agent') ch = 'direct';
      return { ...prev, agent_id: agentId, default_channel: ch };
    });
  };

  const handleDistributorChange = (e) => {
    const distId = e.target.value;
    setForm(prev => {
      let ch = prev.default_channel;
      if (distId) ch = 'via_distributor';
      else if (prev.agent_id) ch = 'via_agent';
      else if (ch === 'via_distributor') ch = 'direct';
      return { ...prev, distributor_id: distId, default_channel: ch };
    });
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const res = await api.get(`/pincode/${pincode}`);
      setAvailableAreas(res.data.areas || []);
      setForm(prev => ({ ...prev, area: res.data.area || '', city: res.data.city || '', state: res.data.state || '', country: 'India' }));
      toast.success(`Auto-filled: ${res.data.city}, ${res.data.state}`);
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.city.trim()) { toast.error('City is required'); return; }
    const payload = { ...form };
    // Discount is optional — blank means 0
    payload.discount_percent = form.discount_percent === '' || form.discount_percent === null ? 0 : parseFloat(form.discount_percent);
    if (!payload.agent_id) delete payload.agent_id;
    if (!payload.distributor_id) delete payload.distributor_id;
    try {
      if (editingCust) {
        await api.put(`/customers/${editingCust.id}`, payload);
        toast.success('Customer updated!');
        setIsDialogOpen(false); resetForm(); loadData();
      } else {
        const res = await api.post('/customers', payload);
        toast.success('Customer created!');
        setIsDialogOpen(false); resetForm(); loadData();
        if (returnToSO.current) {
          returnToSO.current = false;
          navigateTo('/sales-orders', { state: { fromCustomerCreate: true, newCustomerId: res.data.id } });
        }
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save customer'); }
  };

  const startEdit = (cust) => {
    setEditingCust(cust);
    setForm({
      company_name: cust.company_name, contact_person: cust.contact_person, phone: cust.phone,
      accountant_phone: cust.accountant_phone || '', email: cust.email || '',
      address: cust.address || '', area: cust.area || '', city: cust.city || '', state: cust.state || '', country: cust.country || 'India',
      pincode: cust.pincode || '', gst_number: cust.gst_number || '',
      transport_preference: cust.transport_preference || '', payment_mode: cust.payment_mode || '', credit_days: cust.credit_days || '', default_channel: cust.default_channel || '', wsp_type: cust.wsp_type || 'all', discount_percent: cust.discount_percent ?? '', agent_id: cust.agent_id || '', distributor_id: cust.distributor_id || '',
    });
    setIsDialogOpen(true);
  };

  const deleteCust = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try { await api.delete(`/customers/${id}`); toast.success('Customer deleted'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const resetForm = () => {
    setForm({ company_name: '', contact_person: '', phone: '', accountant_phone: '', email: '', address: '', area: '', city: '', state: '', country: 'India', pincode: '', gst_number: '', transport_preference: '', payment_mode: '', credit_days: '', wsp_type: 'all', discount_percent: '', agent_id: '', distributor_id: '' });
    setEditingCust(null); setAvailableAreas([]);
  };

  const handleQuickAddAgent = async () => {
    if (!newAgent.name || !newAgent.phone) { toast.error('Name and phone are required'); return; }
    try {
      const res = await api.post('/agents', { name: newAgent.name, phone: newAgent.phone, email: '', area: '', city: '', state: '', commission_percentage: 0 });
      toast.success(`Agent "${newAgent.name}" added!`);
      const agentRes = await api.get('/agents');
      setAgents(agentRes.data);
      setForm(prev => ({ ...prev, agent_id: res.data.id }));
      setShowAddAgent(false);
      setNewAgent({ name: '', phone: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add agent'); }
  };

  const getAgentName = (id) => agents.find(a => a.id === id)?.name || '';
  const getDistName = (id) => distributors.find(d => d.id === id)?.company_name || '';
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'salesman';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const handleDownload = async (format) => {
    try {
      const res = await api.get(`/downloads/customers/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `customers.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`; };

  // Derive unique filter options from data
  const stateOptions = [...new Set(customers.map(c => c.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const cityOptions = [...new Set(customers.filter(c => !filterStates.length || filterStates.includes(c.state)).map(c => c.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const agentOptions = [{ value: '__direct__', label: 'Direct' }, ...agents.filter(a => a.is_active).map(a => ({ value: a.id, label: a.name })).sort((a, b) => a.label.localeCompare(b.label))];
  const distributorOptions = [{ value: '__direct__', label: 'Direct' }, ...distributors.filter(d => d.is_active).map(d => ({ value: d.id, label: d.company_name })).sort((a, b) => a.label.localeCompare(b.label))];
  const paymentModeOptions = [...new Set(customers.map(c => c.payment_mode).filter(Boolean))].sort().map(p => ({ value: p, label: formatPaymentMode(p) }));
  const creditDaysOptions = [...new Set(customers.filter(c => c.payment_mode === 'credit' && c.credit_days).map(c => c.credit_days))].sort().map(d => ({ value: d, label: `${d} days` }));
  const showCreditDaysFilter = filterPaymentModes.includes('credit');
  const hasActiveFilters = !!(searchTerm || filterStates.length || filterCities.length || filterAgents.length || filterDistributors.length || filterPaymentModes.length || filterCreditDays.length);

  const restricted = isRestricted(user);
  const filtered = restricted
    ? applyRestrictedSearch(user, customers.map(c => ({ ...c, name: c.company_name })), searchTerm)
    : customers.filter(c => {
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const agentName = (agents.find(a => a.id === c.agent_id)?.name || '').toLowerCase();
          const distName = (distributors.find(d => d.id === c.distributor_id)?.company_name || '').toLowerCase();
          if (!c.company_name?.toLowerCase().includes(q) && !c.contact_person?.toLowerCase().includes(q) && !c.phone?.includes(searchTerm) && !(c.city || '').toLowerCase().includes(q) && !(c.area || '').toLowerCase().includes(q) && !(c.state || '').toLowerCase().includes(q) && !agentName.includes(q) && !distName.includes(q)) return false;
        }
        if (filterStates.length && !filterStates.includes(c.state)) return false;
        if (filterCities.length && !filterCities.includes(c.city)) return false;
        if (filterAgents.length) {
          const isDirect = filterAgents.includes('__direct__');
          const agentIds = filterAgents.filter(a => a !== '__direct__');
          if (!(isDirect && !c.agent_id) && !agentIds.includes(c.agent_id)) return false;
        }
        if (filterDistributors.length) {
          const isDirect = filterDistributors.includes('__direct__');
          const distIds = filterDistributors.filter(d => d !== '__direct__');
          if (!(isDirect && !c.distributor_id) && !distIds.includes(c.distributor_id)) return false;
        }
        if (filterPaymentModes.length && !filterPaymentModes.includes(c.payment_mode)) return false;
        if (filterCreditDays.length && !filterCreditDays.includes(c.credit_days)) return false;
        return true;
      }).sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="customer-management-container">
      {restricted && <SecurityWatermark user={user} label="CUSTOMERS • CONFIDENTIAL" />}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Customers</h1>
          <p className="text-gray-600 mt-1">Manage your customer database</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50 gap-1.5" onClick={() => handleDownload('excel')} data-testid="download-customers-excel">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Excel
              </Button>
              <Button variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50 gap-1.5" onClick={() => handleDownload('pdf')} data-testid="download-customers-pdf">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF
              </Button>
            </>
          )}
        {canManage && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCust ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
                <DialogDescription>Customer company and contact details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Company Name *</Label><Input name="company_name" value={form.company_name} onChange={handleChange} required data-testid="cust-company-input" /></div>
                  <div className="space-y-2"><Label>Contact Person *</Label><Input name="contact_person" value={form.contact_person} onChange={handleChange} required data-testid="cust-contact-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Phone *</Label><Input name="phone" value={form.phone} onChange={handleChange} required data-testid="cust-phone-input" /></div>
                  <div className="space-y-2"><Label>Accountant Phone</Label><Input name="accountant_phone" value={form.accountant_phone} onChange={handleChange} data-testid="cust-acct-phone-input" /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" value={form.email} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Address</Label><Input name="address" value={form.address} onChange={handleChange} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pincode (auto-fills area/city/state)</Label>
                    <Input name="pincode" value={form.pincode} onChange={handleChange} maxLength="6" />
                  </div>
                  <div className="space-y-2">
                    <Label>Area</Label>
                    {availableAreas.length > 0 ? (
                      <select name="area" value={form.area} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                        {availableAreas.map((a, i) => <option key={i} value={a}>{a}</option>)}
                      </select>
                    ) : <Input name="area" value={form.area} onChange={handleChange} />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <SearchableSelect value={form.country} onChange={(v) => setField('country', v)}
                      options={COUNTRIES} placeholder="Select country" searchPlaceholder="Search country..."
                      allowCustom testId="cust-country-select" />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <SearchableSelect value={form.state} onChange={handleStateChange}
                      options={INDIAN_STATES} placeholder="Select state" searchPlaceholder="Search state..."
                      allowCustom testId="cust-state-select" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <SearchableSelect value={form.city} onChange={(v) => {
                    setField('city', v);
                    const autoState = getStateForCity(v);
                    if (autoState && !form.state) setField('state', autoState);
                  }}
                    options={[...new Set(customers.filter(c => !form.state || c.state === form.state).map(c => c.city).filter(Boolean))].sort()}
                    placeholder="Select city" searchPlaceholder="Search or type city..."
                    allowCustom testId="cust-city-select" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>GST Number</Label><Input name="gst_number" value={form.gst_number} onChange={handleChange} data-testid="cust-gst-input" /></div>
                  <div className="space-y-2"><Label>Transport Preference</Label><Input name="transport_preference" value={form.transport_preference} onChange={handleChange} placeholder="e.g. VRL Logistics" data-testid="cust-transport-input" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <select name="payment_mode" value={form.payment_mode} onChange={e => { handleChange(e); if (e.target.value !== 'credit') setForm(prev => ({ ...prev, credit_days: '' })); }} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cust-payment-mode">
                    <option value="">Select Payment Mode</option>
                    <option value="advance">Advance</option>
                    <option value="cash">Cash</option>
                    <option value="pdc_cheque">PDC Cheque</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                {form.payment_mode === 'credit' && (
                  <div className="space-y-2">
                    <Label>No of Days *</Label>
                    <Input name="credit_days" value={form.credit_days} onChange={handleChange} required placeholder="e.g. 30, 45, 60" data-testid="cust-credit-days" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Default Channel</Label>
                  <select name="default_channel" value={form.default_channel} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cust-default-channel">
                    <option value="">Select Channel</option>
                    <option value="direct">Direct</option>
                    <option value="via_agent">Via Agent</option>
                    <option value="via_distributor">Via Distributor</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>WSP Type</Label>
                    <select name="wsp_type" value={form.wsp_type} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cust-wsp-type">
                      <option value="local">Local WSP</option>
                      <option value="all">All India WSP</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount %</Label>
                    <Input type="number" name="discount_percent" value={form.discount_percent} onChange={handleChange} placeholder="e.g. 5, 10" min="0" max="100" step="0.5" data-testid="cust-discount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agent</Label>
                    <div className="flex gap-1">
                      <select name="agent_id" value={form.agent_id} onChange={handleAgentChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cust-agent-select">
                        <option value="">Direct</option>
                        {[...agents].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      <Button type="button" size="sm" variant="outline" className="h-10 px-2 shrink-0 text-violet-600 border-violet-200 hover:bg-violet-50"
                        onClick={() => setShowAddAgent(true)} data-testid="quick-add-agent-btn" title="Add new agent">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      </Button>
                    </div>
                    {showAddAgent && (
                      <div className="border rounded-lg p-3 bg-violet-50 space-y-2 mt-1" data-testid="quick-add-agent-form">
                        <p className="text-xs font-semibold text-violet-700">Quick Add Agent</p>
                        <Input placeholder="Agent Name *" value={newAgent.name} onChange={e => setNewAgent(p => ({...p, name: e.target.value}))} className="h-8 text-xs" data-testid="quick-agent-name" />
                        <Input placeholder="Phone *" value={newAgent.phone} onChange={e => setNewAgent(p => ({...p, phone: e.target.value}))} className="h-8 text-xs" data-testid="quick-agent-phone" />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={handleQuickAddAgent} data-testid="quick-agent-save">Save</Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowAddAgent(false); setNewAgent({ name: '', phone: '' }); }}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Distributor</Label>
                    <select name="distributor_id" value={form.distributor_id} onChange={handleDistributorChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cust-distributor-select">
                      <option value="">Direct</option>
                      {[...distributors].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')).map(d => <option key={d.id} value={d.id}>{d.company_name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1" data-testid="submit-cust-button">{editingCust ? 'Update Customer' : 'Create Customer'}</Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card data-testid="customer-filters">
        <CardContent className="p-4 space-y-3">
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search company, contact, city, state, agent, distributor..." className="max-w-md" data-testid="customer-search-input" />
          <div className="flex flex-wrap gap-2 items-center">
            <MultiSelectFilter label="State" options={stateOptions} selected={filterStates} onChange={setFilterStates} testId="cust-filter-state" />
            <MultiSelectFilter label="City" options={cityOptions} selected={filterCities} onChange={setFilterCities} testId="cust-filter-city" />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={filterAgents} onChange={setFilterAgents} testId="cust-filter-agent" />
            <MultiSelectFilter label="Distributor" options={distributorOptions} selected={filterDistributors} onChange={setFilterDistributors} testId="cust-filter-distributor" />
            <MultiSelectFilter label="Payment Mode" options={paymentModeOptions} selected={filterPaymentModes} onChange={(v) => { setFilterPaymentModes(v); if (!v.includes('credit')) setFilterCreditDays([]); }} testId="cust-filter-payment" />
            {showCreditDaysFilter && creditDaysOptions.length > 0 && (
              <MultiSelectFilter label="Credit Days" options={creditDaysOptions} selected={filterCreditDays} onChange={setFilterCreditDays} testId="cust-filter-credit-days" />
            )}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => { setSearchTerm(''); setFilterStates([]); setFilterCities([]); setFilterAgents([]); setFilterDistributors([]); setFilterPaymentModes([]); setFilterCreditDays([]); }} data-testid="cust-clear-filters">
                Clear All
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500">Showing {filtered.length} of {customers.length} customers</p>}
        </CardContent>
      </Card>

      <Card data-testid="customers-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Customers ({filtered.length})</CardTitle>
            {canManage && (
              <Button size="sm" className="bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 shadow-sm" onClick={() => setIsDialogOpen(true)} data-testid="add-customer-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add Customer
              </Button>
            )}
          </div>
          <CardDescription>Your customer database</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="customers-empty">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              {restricted ? (
                (searchTerm || '').trim().length < MIN_SEARCH_CHARS ? (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Required</h3>
                    <p className="text-gray-600">Type at least {MIN_SEARCH_CHARS} characters to look up a customer.</p>
                    <p className="text-xs text-gray-400 mt-2">For your role, the full customer list is not displayed. Up to 10 search results are shown.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Match</h3>
                    <p className="text-gray-600">No customer matches "{searchTerm}".</p>
                  </>
                )
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{searchTerm ? 'No Customers Found' : 'No Customers Yet'}</h3>
                  <p className="text-gray-600">{searchTerm ? 'Try a different search term.' : 'Add your first customer.'}</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customers-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filtered.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="p-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Contact</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Phone</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Location</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Agent</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Distributor</th>
                    <th className="p-3 text-left font-semibold text-gray-600">GST</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(cust => (
                    <tr key={cust.id} className={`border-t hover:bg-gray-50 ${bulk.isSelected(cust.id) ? "bg-blue-50" : ""}`} data-testid={`cust-row-${cust.id}`}>
                    <td className="p-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(cust.id)} onChange={() => bulk.toggleSelect(cust.id)} /></td>
                      <td className="p-3">
                        <div className="font-semibold text-gray-900">{cust.company_name}</div>
                        {cust.transport_preference && <span className="text-xs text-gray-400">Transport: {cust.transport_preference}</span>}
                      </td>
                      <td className="p-3 text-gray-700">{cust.contact_person}</td>
                      <td className="p-3">
                        <div className="text-gray-700">{restricted ? maskPhone(cust.phone) : cust.phone}</div>
                        {cust.accountant_phone && <div className="text-xs text-gray-400">Acct: {cust.accountant_phone}</div>}
                      </td>
                      <td className="p-3 text-gray-500 text-xs">{[cust.city, cust.state].filter(Boolean).join(', ')}{cust.pincode ? ` - ${cust.pincode}` : ''}</td>
                      <td className="p-3">{cust.agent_id ? <Badge className="bg-violet-100 text-violet-700 text-xs">{getAgentName(cust.agent_id)}</Badge> : <span className="text-gray-400 text-xs">Direct</span>}</td>
                      <td className="p-3">{cust.distributor_id ? <Badge className="bg-orange-100 text-orange-700 text-xs">{getDistName(cust.distributor_id)}</Badge> : <span className="text-gray-400 text-xs">Direct</span>}</td>
                      <td className="p-3 text-xs text-gray-500">{restricted ? (cust.gst_number ? maskGST(cust.gst_number) : '-') : (cust.gst_number || '-')}</td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-800 h-7 px-2" onClick={() => viewHistory(cust.id)} data-testid={`history-cust-${cust.id}`} title="View History">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </Button>
                          {canManage && (
                            <>
                              <Button size="sm" variant="ghost" className="text-blue-500 hover:text-blue-700 h-7 px-2" onClick={() => startEdit(cust)} data-testid={`edit-cust-${cust.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </Button>
                              <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" onClick={() => navigateTo(`/ledger-report?type=customer&id=${cust.id}`)} data-testid={`ledger-cust-${cust.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 px-2" onClick={() => deleteCust(cust.id)} data-testid={`delete-cust-${cust.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{historyData?.customer?.company_name || 'Customer'} — Order History</DialogTitle>
            <DialogDescription>Complete order timeline, stats, and favourite designs</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="p-12 text-center text-gray-400">Loading history...</div>
          ) : historyData ? (
            <CustomerHistoryContent data={historyData} restricted={restricted} />
          ) : null}
        </DialogContent>
      </Dialog>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="CUSTOMER" label="customers" onComplete={() => { bulk.clearSelection(); fetchCustomers(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

const fmtAmt = (v) => {
  if (!v) return '0';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const TYPE_COLORS = {
  SO: 'bg-indigo-100 text-indigo-800',
  SE: 'bg-emerald-100 text-emerald-800',
  SR: 'bg-red-100 text-red-800',
};

const CustomerHistoryContent = ({ data, restricted }) => {
  const { customer, stats, favourite_designs, timeline } = data;
  const fDate = (d) => {
    if (!d) return '—';
    const parts = d.split('-');
    if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
    return d;
  };

  return (
    <div className="space-y-5" data-testid="customer-history-content">
      {/* Customer Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div><span className="text-gray-400">Contact:</span> <span className="font-semibold">{customer.contact_person}</span></div>
        <div><span className="text-gray-400">Phone:</span> <span className="font-semibold">{restricted ? maskPhone(customer.phone) : customer.phone}</span></div>
        <div><span className="text-gray-400">Location:</span> <span className="font-semibold">{[customer.area, customer.city, customer.state].filter(Boolean).join(', ')}</span></div>
        <div><span className="text-gray-400">GST:</span> <span className="font-semibold">{restricted ? (customer.gst_number ? maskGST(customer.gst_number) : '—') : (customer.gst_number || '—')}</span></div>
        <div><span className="text-gray-400">Agent:</span> <span className="font-semibold">{customer.agent_name || 'Direct'}</span></div>
        <div><span className="text-gray-400">Distributor:</span> <span className="font-semibold">{customer.distributor_name || 'Direct'}</span></div>
        <div><span className="text-gray-400">Payment:</span> <span className="font-semibold">{customer.payment_mode || '—'}{customer.credit_days ? ` (${customer.credit_days}d)` : ''}</span></div>
        <div><span className="text-gray-400">WSP:</span> <span className="font-semibold">{customer.wsp_type || '—'}{customer.discount_percent ? ` -${customer.discount_percent}%` : ''}</span></div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="history-stats">
        <Card className="border-l-4 border-l-indigo-500"><CardContent className="p-2.5">
          <p className="text-[10px] text-gray-500">Total Orders</p>
          <p className="text-xl font-bold text-indigo-700">{stats.total_orders}</p>
          <p className="text-[10px] text-gray-400">Last: {fDate(stats.last_order_date)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-2.5">
          <p className="text-[10px] text-gray-500">SO Amount</p>
          <p className="text-xl font-bold text-emerald-700">{fmtAmt(stats.total_so_amount)}</p>
          <p className="text-[10px] text-gray-400">Avg: {fmtAmt(stats.avg_order_value)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-blue-500"><CardContent className="p-2.5">
          <p className="text-[10px] text-gray-500">Dispatched</p>
          <p className="text-xl font-bold text-blue-700">{fmtAmt(stats.total_se_amount)}</p>
          <p className="text-[10px] text-gray-400">{stats.total_dispatches} entries</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardContent className="p-2.5">
          <p className="text-[10px] text-gray-500">Returns</p>
          <p className="text-xl font-bold text-red-700">{fmtAmt(stats.total_sr_amount)}</p>
          <p className="text-[10px] text-gray-400">{stats.total_returns} returns</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-amber-500"><CardContent className="p-2.5">
          <p className="text-[10px] text-gray-500">Net Business</p>
          <p className="text-xl font-bold text-amber-700">{fmtAmt(stats.net_business)}</p>
          <p className="text-[10px] text-gray-400">{stats.total_qty} pcs total</p>
        </CardContent></Card>
      </div>

      {/* Favourite Designs */}
      {favourite_designs.length > 0 && (
        <div data-testid="history-fav-designs">
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Top Designs (by qty ordered)</p>
          <div className="flex flex-wrap gap-2">
            {favourite_designs.map((d, i) => (
              <div key={d.design_code} className="bg-gray-50 border rounded-lg px-3 py-2 text-xs min-w-[130px]">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400 font-mono text-[10px]">#{i + 1}</span>
                  <span className="font-bold text-gray-900">{d.design_code}</span>
                </div>
                <div className="flex gap-3 mt-1">
                  <span className="text-emerald-600">Qty: <strong>{d.qty}</strong></span>
                  <span className="text-amber-600">Amt: <strong>{fmtAmt(d.amount)}</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div data-testid="history-timeline">
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Order Timeline ({timeline.length} events)</p>
        {timeline.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">No orders yet for this customer.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left font-semibold text-gray-500">Type</th>
                  <th className="p-2 text-left font-semibold text-gray-500">Number</th>
                  <th className="p-2 text-left font-semibold text-gray-500">Date</th>
                  <th className="p-2 text-left font-semibold text-gray-500">Status</th>
                  <th className="p-2 text-right font-semibold text-gray-500">Amount</th>
                  <th className="p-2 text-right font-semibold text-gray-500">Items</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((t, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`timeline-row-${i}`}>
                    <td className="p-2"><Badge className={`text-[10px] ${TYPE_COLORS[t.type] || 'bg-gray-100'}`}>{t.type}</Badge></td>
                    <td className="p-2 font-semibold text-gray-900">{t.number}</td>
                    <td className="p-2 text-gray-500">{fDate(t.date)}</td>
                    <td className="p-2"><Badge className="text-[10px] bg-gray-100 text-gray-600">{t.status}{t.approval ? ` (${t.approval})` : ''}</Badge></td>
                    <td className="p-2 text-right font-mono font-semibold text-emerald-700">{fmtAmt(t.amount)}</td>
                    <td className="p-2 text-right text-gray-500">{t.item_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerManagement;
